import useEditTeamName from '@/hooks/useEditTeamName'
import { useGetUserInvitations } from '@/hooks/useGetMyInvites'
import { useTeamMembers } from '@/hooks/useTeamMembers'
import { Edit, X } from '@mynaui/icons-react'
import EmptyState from './EmptyState'
import UserProfileDialogTeamsInviteUserSection from './UserProfileDialogTeamsInviteUserSection'
import UserProfileDialogTeamsMemberListSection from './UserProfileDialogTeamsMemberListSection'
import UserProfileDialogTeamsSeatPurchaseSection from './UserProfileDialogTeamsSeatPurchaseSection'
import { Stack, Text } from './custom-components'
import { Loader } from './custom-components/Loader'
import { Button } from './shadcn/button'
import { Input } from './shadcn/input'
import { Skeleton } from './shadcn/skeleton'

const UserProfileDialogTeamsSection = () => {
  const {
    editTeamNameMode,
    setEditTeamNameMode,
    newTeamName,
    setNewTeamName,
    userTeams,
    isFetchingUserTeams,
    user,
    editTeam,
    isEditingTeam,
  } = useEditTeamName()
  const { currentUserCanEditTeamStuff } = useTeamMembers()
  const { isLoadingTeamInvites, userInvitations } = useGetUserInvitations()

  // Header rendering
  const renderHeader = () => {
    if (isFetchingUserTeams) {
      return (
        <div className="flex justify-center items-center px-6 py-5 w-full mt-[300px]">
          <Loader />
        </div>
      )
    }

    const hasTeams = userTeams && userTeams.length > 0

    if (!hasTeams) {
      return (
        <div className="flex justify-between items-center px-6 py-5 border-b-[1px] border-themesidebarborder dark:border-themesidebarborderdark">
          <Text size="xl" weight="semibold" className="leading-0">
            Team
          </Text>
        </div>
      )
    }

    // User has teams
    const teamName = userTeams.find((team) => team.id === user?.teamId)?.name
    return (
      <div className="flex justify-between items-center px-6 py-5 border-b-[1px] border-themesidebarborder dark:border-themesidebarborderdark">
        {editTeamNameMode ? (
          <div className="flex items-center gap-2">
            <Input
              value={newTeamName}
              placeholder="Enter team name"
              onChange={(e) => setNewTeamName(e.target.value)}
              disabled={isEditingTeam}
            />
            <Button
              onClick={() => editTeam({ name: newTeamName.trim() })}
              size="sm"
              disabled={isEditingTeam}
              loading={isEditingTeam}
            >
              Done
            </Button>
            <Button variant="ghost" size="iconSm" disabled={isEditingTeam} onClick={() => setEditTeamNameMode(false)}>
              <X className="min-w-6" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Text size="xl" weight="semibold" className="leading-0">
              {teamName}
            </Text>
            {currentUserCanEditTeamStuff && (
              <Button variant="ghost" size="icon" onClick={() => setEditTeamNameMode(true)}>
                <Edit className="min-w-6" />
              </Button>
            )}
          </div>
        )}
      </div>
    )
  }

  // Invitations rendering
  const renderInvitations = () => {
    if (!isLoadingTeamInvites && !userInvitations) return null

    let content
    if (isLoadingTeamInvites) {
      content = Array.from({ length: 5 }).map((_, idx) => (
        <div key={idx} className="flex justify-between items-center gap-10 w-full">
          <div className="flex items-center gap-3 w-full">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Stack className="gap-2 w-full">
              <Skeleton className="h-4 w-full max-w-[250px]" />
              <Skeleton className="h-4 w-full max-w-[200px]" />
            </Stack>
          </div>
        </div>
      ))
    } else if (userInvitations && userInvitations.length === 0) {
      content = <EmptyState text="You currently have no team invites" />
    } else {
      content = (userInvitations ?? []).map((invite) => (
        <div key={invite.teamId} className="flex justify-between items-center gap-10">
          <div className="flex items-center gap-3">
            <img
              className="min-w-8 w-8 h-8 rounded-full"
              src={invite.teamAvatar ?? '/bg_gradient.jpg'}
              alt={`${invite.teamName} avatar`}
            />
            <Text size="sm" weight="medium">
              {invite.teamName}
            </Text>
          </div>
          <div className="flex items-center">
            <Button variant="destructivePurple">Accept Invite</Button>
            <Button variant="secondary">Decline</Button>
          </div>
        </div>
      ))
    }

    return <Stack className="gap-4">{content}</Stack>
  }

  return (
    <Stack className="gap-6">
      {renderHeader()}

      {userTeams && userTeams.length > 0 && (
        <Stack className="gap-4 px-6 pb-5">
          <UserProfileDialogTeamsInviteUserSection />
          <UserProfileDialogTeamsSeatPurchaseSection />
          {user?.teamId && <UserProfileDialogTeamsMemberListSection teamId={user.teamId} />}
        </Stack>
      )}

      <Stack className="gap-4 px-6 py-5">{renderInvitations()}</Stack>
    </Stack>
  )
}

export default UserProfileDialogTeamsSection
